import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import * as maplibregl from 'maplibre-gl';
import { AlertTriangle, ChevronRight, CheckCircle, Navigation2, Signpost } from 'lucide-react';
import type { TrafficIncident } from '../../types';
import type { DashboardLocation } from '../../hooks/useDashboardLocation';
import { MAP_STYLE_URL, createDotElement } from '../../lib/mapLibre';

const SEVERITY_STYLE: Record<string, string> = {
  high: 'bg-red-100 text-red-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-green-100 text-green-700',
  unknown: 'bg-gray-100 text-gray-500',
};

const SEVERITY_COLOR: Record<string, string> = {
  high: '#dc2626',
  medium: '#d97706',
  low: '#16a34a',
  unknown: '#6b7280',
};

// Plots every nearby incident as a severity-coloured pin on one MapLibre map (same
// dynamic-init pattern as TrafficIncidentMap.tsx/RelayMap.tsx). Clicking a pin "focuses" it —
// onFocus lifts the selected incident up to TrafficNewsSection so the detail panel below the
// map can render it, rather than each marker owning its own popup state.
function TrafficNewsMap({
  incidents, focusedId, onFocus,
}: {
  incidents: TrafficIncident[];
  focusedId: string | null;
  onFocus: (id: string) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markersRef = useRef<Map<string, { marker: maplibregl.Marker; el: HTMLDivElement }>>(new Map());
  const onFocusRef = useRef(onFocus);
  onFocusRef.current = onFocus;

  // Build the map + markers once.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: MAP_STYLE_URL,
      center: [0, 0],
      zoom: 5,
      scrollZoom: false,
    });
    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }));

    map.on('load', () => {
      const bounds = new maplibregl.LngLatBounds();
      const popup = new maplibregl.Popup({ closeButton: false, closeOnClick: false, offset: 14 });

      incidents.forEach(incident => {
        const color = SEVERITY_COLOR[incident.severity] ?? SEVERITY_COLOR.unknown;
        const el = createDotElement(color);
        el.addEventListener('mouseenter', () =>
          popup.setLngLat([incident.lng, incident.lat]).setText(incident.title).addTo(map));
        el.addEventListener('mouseleave', () => popup.remove());
        el.addEventListener('click', () => onFocusRef.current(incident.id));

        const marker = new maplibregl.Marker({ element: el })
          .setLngLat([incident.lng, incident.lat])
          .addTo(map);
        markersRef.current.set(incident.id, { marker, el });
        bounds.extend([incident.lng, incident.lat]);
      });

      if (incidents.length === 1) {
        map.setCenter([incidents[0].lng, incidents[0].lat]);
        map.setZoom(12);
      } else if (incidents.length > 1) {
        map.fitBounds(bounds, { padding: 30 });
      }

      setTimeout(() => map.resize(), 150);
    });

    return () => {
      mapRef.current?.remove();
      mapRef.current = null;
      markersRef.current.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Highlight whichever marker is focused — bigger dot with a thicker ring, doesn't rebuild the map.
  useEffect(() => {
    markersRef.current.forEach(({ el }, id) => {
      const focused = id === focusedId;
      el.style.width = focused ? '24px' : '18px';
      el.style.height = focused ? '24px' : '18px';
      el.style.borderWidth = focused ? '4px' : '2px';
      el.style.zIndex = focused ? '10' : '1';
    });
  }, [focusedId]);

  return <div ref={containerRef} data-testid="dashboard-traffic-news-map" className="w-full h-[26rem]" />;
}

// Detail panel shown below the map for whichever incident is currently focused (clicked on the
// map). Mirrors TrafficNewsDetailPage.tsx's content but inline, so drivers don't have to leave the
// dashboard just to read the full description — a link to the full page (with its own larger map)
// is still offered for anyone who wants that.
function FocusedIncidentPanel({ incident }: { incident: TrafficIncident }) {
  const sev = SEVERITY_STYLE[incident.severity] ?? SEVERITY_STYLE.unknown;
  return (
    <div data-testid="dashboard-traffic-news-focused-panel" className="p-4 border-t border-gray-100 space-y-2">
      <div className="flex items-center gap-2">
        <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full capitalize ${sev}`}>{incident.severity}</span>
        <span className="text-xs text-gray-400">{incident.category}</span>
      </div>
      <h3 className="text-sm font-bold text-gray-900">{incident.title}</h3>
      {incident.roadName && (
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <Signpost className="w-3.5 h-3.5 text-gray-400 shrink-0" /> {incident.roadName}
        </div>
      )}
      <div className="flex items-center gap-2 text-xs text-gray-600">
        <Navigation2 className="w-3.5 h-3.5 text-gray-400 shrink-0" /> {incident.distanceMiles.toFixed(1)} miles away
      </div>
      <p className="text-xs text-gray-700 leading-relaxed">{incident.description}</p>
      <Link
        to={`/traffic-news/${encodeURIComponent(incident.id)}`}
        state={incident}
        className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:underline"
      >
        Full details <ChevronRight className="w-3 h-3" />
      </Link>
    </div>
  );
}

// Dashboard section — plots every live UK traffic incident/roadworks near the user's location(s)
// (see useDashboardLocation — derived automatically from the profile address, or from all of a
// carrier's enabled Job Alert areas) on a map, sourced from National Highways' DATEX II feed via
// the backend's TrafficNewsController. Clicking a pin focuses it and expands a detail panel below
// the map (see FocusedIncidentPanel) rather than navigating away. Data is fetched once in
// UserDashboardPage.tsx, mirroring how JobsPanel/PaymentsPanel/etc. receive already-fetched data
// as props rather than each panel calling useQuery itself.
export function TrafficNewsSection({
  location, incidents, isLoading, error,
}: {
  location: DashboardLocation;
  incidents: TrafficIncident[];
  isLoading: boolean;
  error: unknown;
}) {
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const radii = new Set(location.trafficLocations.map(l => l.radiusMiles));
  const scopeLabel = location.areaCount > 1
    ? `across ${location.areaCount} alert areas`
    : radii.size === 1
      ? `within ${[...radii][0]} miles`
      : 'nearby';

  // Keep the focused pin valid as `incidents` refetches (e.g. pull-to-refresh) — fall back to the
  // nearest incident so the panel doesn't silently point at a stale id.
  useEffect(() => {
    if (incidents.length === 0) { setFocusedId(null); return; }
    if (!focusedId || !incidents.some(i => i.id === focusedId)) setFocusedId(incidents[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incidents]);

  const focused = incidents.find(i => i.id === focusedId) ?? null;

  return (
    <div data-testid="dashboard-traffic-news-section" className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
      <div className="px-5 py-3.5 flex items-center gap-2 border-b border-gray-100">
        <AlertTriangle className="w-4 h-4 text-gray-700" />
        <h2 className="font-bold text-sm text-gray-900">Traffic News</h2>
        {location.trafficLocations.length > 0 && (
          <span className="text-xs text-gray-400">· {scopeLabel}</span>
        )}
      </div>

      {!location.loading && location.trafficLocations.length === 0 && (
        <p className="p-4 text-sm text-gray-400">Add an address to your profile to see nearby traffic news.</p>
      )}

      {location.trafficLocations.length > 0 && (location.loading || isLoading) && (
        <p className="p-4 text-sm text-gray-400">Loading traffic news…</p>
      )}

      {location.trafficLocations.length > 0 && !location.loading && !isLoading && !!error && (
        <p data-testid="dashboard-traffic-news-error" className="p-4 text-sm text-red-500">
          Couldn't load traffic news. Refresh to try again.
        </p>
      )}

      {location.trafficLocations.length > 0 && !location.loading && !isLoading && !error && incidents.length === 0 && (
        <div className="py-6 text-center">
          <CheckCircle className="w-7 h-7 text-green-300 mx-auto mb-1.5" />
          <p className="text-sm text-gray-400">No traffic incidents nearby</p>
        </div>
      )}

      {location.trafficLocations.length > 0 && !location.loading && !isLoading && !error && incidents.length > 0 && (
        <>
          <TrafficNewsMap incidents={incidents} focusedId={focusedId} onFocus={setFocusedId} />
          {focused && <FocusedIncidentPanel incident={focused} />}
        </>
      )}
    </div>
  );
}
